import React, { useState } from 'react';
import { X, Trash2, AlertTriangle, User } from 'lucide-react';
import { deleteUser } from '../lib/admin';
import type { UserStats } from '../types/admin';

interface Props {
  user: UserStats;
  onClose: () => void;
  onDelete: () => void;
}

export default function DeleteUserModal({ user, onClose, onDelete }: Props) {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const expectedConfirmText = 'DELETE USER';
  const isConfirmValid = confirmText === expectedConfirmText;

  const handleDelete = async () => {
    if (!isConfirmValid) {
      setError('Please type DELETE USER to confirm');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await deleteUser(user.user_id, reason);
      onDelete();
      onClose();
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-2xl w-full max-h-[95vh] overflow-y-auto my-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <h3 className="text-lg font-semibold text-white">Delete User Account</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-900/50 text-red-200 p-3 rounded-md">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Critical Warning */}
          <div className="bg-red-900/30 border border-red-800 p-4 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-200 mb-2">⚠️ CRITICAL WARNING: This action is IRREVERSIBLE!</h4>
                <p className="text-red-300 text-sm mb-2">
                  Deleting this user will permanently and completely remove:
                </p>
                <ul className="text-red-300 text-sm space-y-1 list-disc list-inside">
                  <li><strong>User account and authentication data</strong></li>
                  <li><strong>ALL {user.registry_count} gift registries</strong> created by this user</li>
                  <li><strong>ALL registry items, claims, and gift data</strong></li>
                  <li><strong>ALL RSVP responses and event data</strong></li>
                  <li><strong>ALL withdrawal requests and financial records</strong></li>
                  <li><strong>ALL notifications and communication history</strong></li>
                  <li><strong>ALL user profile and personal data</strong></li>
                </ul>
                <p className="text-red-200 text-sm mt-2 font-medium">
                  💰 Financial Impact: LKR {user.total_collected.toFixed(2)} in collected funds will be affected
                </p>
              </div>
            </div>
          </div>

          {/* User Details */}
          <div className="bg-gray-900/50 p-4 rounded-lg">
            <div className="flex items-center space-x-3 mb-3">
              <User className="w-5 h-5 text-white" />
              <h4 className="font-medium text-white">User to Delete</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><span className="text-gray-400">Email:</span></p>
                <p className="text-white font-medium">{user.email}</p>
              </div>
              <div>
                <p><span className="text-gray-400">Name:</span></p>
                <p className="text-white font-medium">{user.full_name || 'Not provided'}</p>
              </div>
              <div>
                <p><span className="text-gray-400">Username:</span></p>
                <p className="text-white font-medium">{user.username || 'Not set'}</p>
              </div>
              <div>
                <p><span className="text-gray-400">Member Since:</span></p>
                <p className="text-white font-medium">{new Date(user.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p><span className="text-gray-400">Last Sign In:</span></p>
                <p className="text-white font-medium">{user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : 'Never'}</p>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="bg-yellow-900/30 border border-yellow-800 p-4 rounded-lg">
            <h4 className="font-medium text-yellow-200 mb-3">💰 Financial Impact Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <p className="text-yellow-300">Active Registries</p>
                <p className="text-white font-bold text-lg">{user.registry_count}</p>
              </div>
              <div className="text-center">
                <p className="text-yellow-300">Total Collected</p>
                <p className="text-white font-bold text-lg">LKR {user.total_collected.toFixed(2)}</p>
              </div>
              <div className="text-center">
                <p className="text-yellow-300">Total Withdrawn</p>
                <p className="text-white font-bold text-lg">LKR {user.total_withdrawn.toFixed(2)}</p>
              </div>
              <div className="text-center">
                <p className="text-yellow-300">Pending Requests</p>
                <p className="text-white font-bold text-lg">{user.pending_requests_count}</p>
              </div>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Reason for User Deletion (Required for audit trail)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-red-500 focus:border-red-500"
              rows={3}
              placeholder="Enter detailed reason for deleting this user account (e.g., policy violation, user request, etc.)"
              required
            />
          </div>

          {/* Confirmation */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Type <span className="font-bold text-red-400 bg-red-900/30 px-2 py-1 rounded">DELETE USER</span> to confirm this irreversible action
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-red-500 focus:border-red-500"
              placeholder="DELETE USER"
            />
          </div>

          {/* Final Warning */}
          <div className="bg-red-900/50 border border-red-700 p-4 rounded-lg">
            <p className="text-red-200 text-sm text-center font-medium">
              ⚠️ This will permanently delete the user and ALL their data. This action CANNOT be undone!
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4 pt-6 border-t border-gray-700">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 text-gray-300 hover:text-white disabled:opacity-50 border border-gray-600 rounded-md hover:bg-gray-700/50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={loading || !isConfirmValid || !reason.trim()}
              className="flex items-center justify-center space-x-2 px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              <span>{loading ? 'Deleting User...' : 'Delete User Account'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}